import React, {useState} from 'react';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {StyleType} from 'patient-ping-remedy/packages/button';
import IconButton, {IconButtonSize} from 'patient-ping-remedy/packages/icon-button';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {cx} from '@emotion/css';
import {BannerBody, BannerMessage, dismissibleBannerClass} from '@common/DismissibleBanner.styles';

type Props = {
  children: React.ReactNode;
  type?: BannerTypes;
  dismissed?: boolean;
  onDismiss?: () => void;
  className?: string;
};

/**
 * A Remedy Banner with a dismiss (X) control, since Remedy's Banner has no native dismiss
 * affordance (CARECO-6086).
 *
 * Controlled iff `dismissed` is passed: the parent owns visibility and `onDismiss` fires on X
 * click. Otherwise uncontrolled — it self-manages dismissed state and renders null once dismissed
 * (if an uncontrolled caller needs it to reappear when inputs change, remount it via a React `key`).
 */
const DismissibleBanner = ({children, type = BannerTypes.WARNING, dismissed, onDismiss, className}: Props) => {
  const [internalDismissed, setInternalDismissed] = useState(false);
  const isControlled = dismissed !== undefined;
  const isDismissed = isControlled ? dismissed : internalDismissed;

  if (isDismissed) return null;

  const handleDismiss = () => {
    if (!isControlled) setInternalDismissed(true);
    onDismiss?.();
  };

  return (
    <Banner type={type} className={cx(dismissibleBannerClass, className)}>
      <BannerBody>
        <BannerMessage>{children}</BannerMessage>
        <IconButton
          iconClass={ICONS.times}
          styleType={StyleType.TERTIARY}
          size={IconButtonSize.SMALL}
          aria-label="Dismiss warning"
          onClick={handleDismiss}
        />
      </BannerBody>
    </Banner>
  );
};

export default DismissibleBanner;
